<template>
  <div class="admin-layout" :class="{ 'sidebar--desktop-collapsed': desktopCollapsed }">
    <Sidebar
      :terbuka="menuTerbuka"
      :collapsed="desktopCollapsed"
      @tutup="tutupSidebar"
    />
    <main class="content">
      <AdminHeader
        class="floating-header with-space"
        :sidebar-terbuka="isDesktop ? !desktopCollapsed : menuTerbuka"
        @toggle-sidebar="toggleSidebar"
      />
      <div class="main-view">
        <PenangkapGalat>
          <router-view v-slot="{ Component }">
            <component :is="Component" :key="$route.fullPath" />
          </router-view>
        </PenangkapGalat>
      </div>
    </main>

    <!--
      Satu dialog konfirmasi untuk seluruh panel admin.
      Menempatkannya di sini, bukan di setiap tabel, berarti komponen mana pun
      cukup memanggil konfirmasi() dari useConfirm tanpa perlu memasang
      dialognya sendiri.
    -->
    <ConfirmDialog
      :open="confirmState.open"
      :title="confirmState.title"
      :message="confirmState.message"
      :item-name="confirmState.itemName"
      :confirm-text="confirmState.confirmText"
      :cancel-text="confirmState.cancelText"
      :tone="confirmState.tone"
      :busy="confirmState.busy"
      @confirm="setuju"
      @cancel="batal"
    />
  </div>
</template>

<script>
import { ref, provide, onMounted, onBeforeUnmount } from 'vue';
import AdminHeader from '@/components/admin/AdminHeader.vue';
import Sidebar from '@/components/admin/AdminSidebar.vue';
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue';
import PenangkapGalat from '@/components/admin/PenangkapGalat.vue';
import { useConfirm } from '@/composables/useConfirm';
import http from '@/services/http';

export default {
  name: 'AdminLayout',

  components: { AdminHeader, Sidebar, ConfirmDialog, PenangkapGalat },

  setup() {
    const permissions = ref([]);

    const fetchPermissions = async () => {
      try {
        // Memakai klien http terpusat: token disisipkan otomatis oleh
        // interceptor, jadi header tidak perlu ditulis ulang di sini.
        const { data } = await http.get('/user/permissions');
        permissions.value = Array.isArray(data) ? data : [];
      } catch (error) {
        permissions.value = [];
      }
    };

    provide('permissions', permissions);
    onMounted(fetchPermissions);

    const { state: confirmState, setuju, batal } = useConfirm();

    /*
    | Status buka/tutup bilah sisi untuk layar sempit (drawer) dan layar lebar (desktop).
    | Preferensi di desktop disimpan di localStorage agar bertahan antar sesi.
    */
    const isDesktop = ref(typeof window !== 'undefined' ? window.innerWidth >= 992 : true);
    const desktopCollapsed = ref(
      typeof window !== 'undefined'
        ? localStorage.getItem('arspo_sidebar_collapsed') === 'true'
        : false
    );
    const menuTerbuka = ref(false);

    const updateViewport = () => {
      if (typeof window !== 'undefined') {
        isDesktop.value = window.innerWidth >= 992;
      }
    };

    onMounted(() => {
      window.addEventListener('resize', updateViewport, { passive: true });
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateViewport);
    });

    const toggleSidebar = () => {
      const isScreenDesktop = typeof window !== 'undefined' ? window.innerWidth >= 992 : true;
      if (isScreenDesktop) {
        desktopCollapsed.value = !desktopCollapsed.value;
        try {
          localStorage.setItem('arspo_sidebar_collapsed', String(desktopCollapsed.value));
        } catch (e) {
          // Abaikan jika penyimpanan lokal diblokir peramban
        }
      } else {
        menuTerbuka.value = !menuTerbuka.value;
      }
    };

    const tutupSidebar = () => {
      const isScreenDesktop = typeof window !== 'undefined' ? window.innerWidth >= 992 : true;
      if (isScreenDesktop) {
        desktopCollapsed.value = true;
        try {
          localStorage.setItem('arspo_sidebar_collapsed', 'true');
        } catch (e) {
          // Abaikan jika penyimpanan lokal diblokir peramban
        }
      } else {
        menuTerbuka.value = false;
      }
    };

    return {
      permissions,
      confirmState,
      setuju,
      batal,
      isDesktop,
      desktopCollapsed,
      menuTerbuka,
      toggleSidebar,
      tutupSidebar,
    };
  },
};
</script>

<style scoped>
.admin-layout {
  display: flex;
  min-height: 100vh;
  background-color: var(--bg-base);
  overflow-x: hidden;
}

.content {
  /* Warna dikunci ke token tema; sebelumnya #F8F7FA membuat panel admin
     tetap putih terang walaupun mode gelap aktif. */
  background-color: var(--bg-base);
  margin-left: 280px;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 0;
  transition: margin-left var(--transition-base) var(--ease-out-quart);
}

@media (max-width: 991.98px) {
  .content { margin-left: 0; }
}

@media (min-width: 992px) {
  .admin-layout.sidebar--desktop-collapsed .content,
  .sidebar--desktop-collapsed .content {
    margin-left: 0 !important;
  }
}

.floating-header {
  position: fixed;
  top: 0;
  left: 280px;
  right: 0 !important;
  height: 56px;
  z-index: 1000;
  background-color: var(--header-bg);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: none;
  border-bottom: 1px solid var(--border-subtle);
  border-radius: 0 !important;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  padding: 0 24px;
  display: flex;
  align-items: center;
  transition: left var(--transition-base) var(--ease-out-quart);
}

@media (min-width: 992px) {
  .admin-layout.sidebar--desktop-collapsed .floating-header,
  .sidebar--desktop-collapsed .floating-header,
  .sidebar--desktop-collapsed :deep(.floating-header) {
    left: 0 !important;
  }
}

@media (max-width: 991.98px) {
  .floating-header {
    top: 0;
    left: 0 !important;
    right: 0 !important;
    height: 54px;
    border-radius: 0 !important;
    border: none;
    border-bottom: 1px solid var(--border-subtle);
    padding: 0 16px;
  }
}

.floating-header.with-space { margin-bottom: 0; }

.main-view {
  background-color: var(--bg-base);
  /* 56px header + 20px space = 76px */
  padding: var(--space-6);
  padding-top: 76px;
  flex: 1;
}

@media (max-width: 991.98px) {
  /* Kepala menempel di atas (tinggi ~54px), beri ruang 66px agar tidak tertutup */
  .main-view { padding: 12px 10px; padding-top: 66px; }
}

@media (max-width: 480px) {
  .main-view { padding: 10px 8px; padding-top: 64px; }
}
</style>
